import {
  MATRIX_PIVOT_LABELS,
  MATRIX_PIVOT_MEANINGS,
  type MatrixDestinataire,
  type MatrixPivot,
  resolveDestinataireLabel,
  resolveDestinataireValue,
} from '@mss_creator/shared';
import { Button, Icon } from '@pms-digital/design-system';
import type { ReactNode } from 'react';

export interface CadrageFrozenChoicesProps {
  pivot: MatrixPivot;
  /** Null = « Non défini » (issue #25). */
  destinataire: MatrixDestinataire | null;
  /** The free text of « Autre » (issue #21); ignored for any other value. */
  destinataireAutre: string | null;
  /** « Nouvelle matrice depuis celle-ci ». The button is left out while no action is wired. */
  onNewFromThis?: (() => void) | undefined;
  newFromThisPending?: boolean | undefined;
  newFromThisError?: string | null | undefined;
}

interface FrozenRowProps {
  label: string;
  value: string;
  hint?: string | undefined;
  /** The value says « nothing chosen »: shown in gray. */
  muted?: boolean | undefined;
}

function FrozenRow({ label, value, hint, muted = false }: FrozenRowProps): ReactNode {
  return (
    <div className="grid grid-cols-1 items-center gap-x-4 gap-y-1 px-4 py-3 sm:grid-cols-[12.5rem_1fr]">
      <dt className="flex items-center gap-2 font-semibold text-small">
        <span className="flex text-gray-500">
          <Icon name="lock" size={16} />
        </span>
        {label}
      </dt>
      <dd className={muted ? 'text-gray-500 text-small' : 'text-small'}>
        {value}
        {hint !== undefined && <span className="text-gray-500"> · {hint}</span>}
      </dd>
    </div>
  );
}

/**
 * The matrix's pivot and recipient in the Cadrage tab (issues #16, #21 and
 * #25, planches 07 and 20): read-only rows, badged « Figés », kept apart from
 * the editable sujet and contexte and from their « Enregistrer ». The
 * recipient row shows what the model receives, in full: the label, or the
 * free text followed by « saisie libre »; « Non défini » in gray.
 *
 * Always frozen here, even on a never-generated draft (règle complémentaire
 * 9): the wizard is the one place they are chosen. Changing them means a new
 * matrix created from this one.
 */
export function CadrageFrozenChoices({
  pivot,
  destinataire,
  destinataireAutre,
  onNewFromThis,
  newFromThisPending = false,
  newFromThisError = null,
}: CadrageFrozenChoicesProps) {
  const fields = { destinataire, destinataireAutre };
  const sent = resolveDestinataireValue(fields);
  return (
    <div data-testid="cadrage-frozen-choices">
      <div className="mb-2">
        <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-0 text-caption text-gray-600">
          <Icon name="lock" size={14} />
          Figés
        </span>
      </div>
      <dl className="divide-y divide-gray-100 overflow-hidden rounded-md border border-gray-200">
        <FrozenRow
          label="Pivot"
          value={MATRIX_PIVOT_LABELS[pivot]}
          hint={MATRIX_PIVOT_MEANINGS[pivot]}
        />
        <FrozenRow
          label="Destinataire"
          value={resolveDestinataireLabel(fields)}
          hint={sent !== null && destinataire === 'autre' ? 'saisie libre' : undefined}
          muted={sent === null}
        />
      </dl>
      <div className="mt-3 flex flex-wrap items-center gap-4">
        <p className="min-w-[17.5rem] flex-1 text-gray-600 text-small">
          Choisi avant la génération. Pour changer, créez une nouvelle matrice depuis celle-ci.
        </p>
        {onNewFromThis && (
          <Button
            type="button"
            variant="primary"
            size="sm"
            icon="library_add"
            onClick={onNewFromThis}
            disabled={newFromThisPending}
          >
            {newFromThisPending ? 'Création…' : 'Nouvelle matrice depuis celle-ci'}
          </Button>
        )}
      </div>
      {newFromThisError && <p className="mt-1 text-danger text-small">{newFromThisError}</p>}
    </div>
  );
}
